import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { LayoutGrid, Globe, Inbox, CheckSquare, MessageCircle, BarChart3, FolderOpen, Sparkles, FileText, Users, ShieldCheck, Bell, LifeBuoy, X, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { SECTION_GUIDE, guideKeyFor } from "./guideContent";
import GettingStarted from "./GettingStarted";
import FirstRecommendation from "./FirstRecommendation";

// Vollstaendiger A-Z-Rundgang: jeder Bereich in Navigationsreihenfolge, jeweils mit konkreter Aktion.
const ALL_STEPS = [
  { key: "overview", icon: LayoutGrid, title: "Überblick", text: "Ihre Startseite. Auf einen Blick sehen Sie den Status Ihrer Website, offene Aufgaben, neue Anfragen, Supportfälle und Rechnungen. Tippen Sie auf eine Kachel, um direkt in den jeweiligen Bereich zu springen." },
  { key: "website", icon: Globe, title: "Website", text: "Prüfen Sie, ob Ihre Website online ist. Mit «Live prüfen» starten Sie sofort einen Check von Erreichbarkeit, SSL-Sicherheit, Backup und Ladezeit. Wird etwas rot, melden Sie sich einfach bei KEWAG." },
  { key: "leads", icon: Inbox, title: "Anfragen", text: "Jede Anfrage über Ihr Kontaktformular landet automatisch hier. Öffnen Sie eine Anfrage, um Name, Nachricht und Kontaktdaten zu sehen, und reagieren Sie direkt per Telefon oder E-Mail — so verpassen Sie keinen Kunden." },
  { key: "tasks", icon: CheckSquare, title: "Aufgaben & Freigaben", text: "Hier sehen Sie, was KEWAG gerade für Sie erledigt und wo Ihre Freigabe fehlt. Prüfen Sie den Vorschlag und tippen Sie auf «Freigeben», damit wir weiterarbeiten können — oder stellen Sie eine kurze Rückfrage." },
  { key: "support", icon: MessageCircle, title: "Support", text: "Ihr direkter Draht zu KEWAG. Beschreiben Sie Ihre Frage oder Ihr Anliegen, hängen Sie bei Bedarf ein Bild an und senden Sie ab. Der KI-Assistent hilft sofort weiter oder leitet Sie an einen Menschen im Team." },
  { key: "performance", icon: BarChart3, title: "Besuche & Performance", text: "Sehen Sie, wie viele Menschen Ihre Website besuchen, woher sie kommen und welche Seiten am beliebtesten sind. Ganz oben finden Sie ein monatliches Highlight in einfachen Worten." },
  { key: "documents", icon: FolderOpen, title: "Dokumente & Rechnungen", text: "Alle Rechnungen, Verträge, Briefings und Dateien an einem Ort. Laden Sie eine Rechnung oder ein Dokument mit einem Tippen als PDF herunter und sehen Sie Ihr Paket sowie Ihre Vertragslaufzeit." },
  { key: "next-steps", icon: Sparkles, title: "Nächste Schritte", text: "Konkrete Empfehlungen von KEWAG, wie Sie mehr aus Ihrer Website herausholen. Lesen Sie den Vorschlag und fordern Sie mit einem Klick ein unverbindliches Angebot an." },
  { key: "report", icon: FileText, title: "Monatsreport", text: "Jeden Monat fassen wir Ihre Ergebnisse einfach verständlich zusammen: Besucher, Anfragen, erledigte Aufgaben und eine Empfehlung. Mit «Als PDF exportieren» sichern Sie den Report für Ihre Unterlagen." },
  { key: "team", icon: Users, title: "Team & Benutzer", text: "Laden Sie Mitarbeitende in Ihr Cockpit ein und legen Sie fest, welche Bereiche sie sehen dürfen. Ideal, wenn mehrere Personen in Ihrem Betrieb mitarbeiten." },
  { key: "security", icon: ShieldCheck, title: "Konto & Sicherheit", text: "Ändern Sie hier Ihr Passwort und aktivieren Sie die Zwei-Faktor-Authentifizierung (2FA) für zusätzlichen Schutz. Wir empfehlen 2FA für alle Zugänge — die Einrichtung dauert weniger als eine Minute." },
  { key: "notifications", icon: Bell, title: "Benachrichtigungen", text: "Über die Glocke oben rechts informiert Sie KEWAG über alles Neue: Aufgaben, Antworten, Dokumente, Rechnungen und Reports. Tippen Sie darauf, um alle Neuigkeiten zu sehen." },
  { key: "contact", icon: LifeBuoy, title: "Ansprechpartner", text: "Ihr persönliches KEWAG-Betreuungsteam. Mit «Nachricht senden» erreichen Sie uns jederzeit direkt aus dem Cockpit. Wir sind von A bis Z für Sie da." },
];

export default function OnboardingTour({ open, initialPhase = "welcome", accent = "#A3E635", navKeys = null, checklist = [], loadRecommendation, onClose, onHighlight, onStart, onComplete, onDismiss, onOpenAi, onGo }) {
  const { t } = useI18n();
  const [phase, setPhase] = useState(initialPhase);
  const [i, setI] = useState(0);

  // Schritte auf die tatsaechlich sichtbaren Bereiche des Benutzers filtern (z. B. eingeschraenkte Mitarbeitende).
  const STEPS = React.useMemo(() => (
    navKeys && navKeys.length
      ? ALL_STEPS.filter((s) => ["notifications", "contact"].includes(s.key) || navKeys.includes(s.key))
      : ALL_STEPS
  ), [navKeys]);

  useEffect(() => {
    if (open) { setPhase(initialPhase); setI(0); if (initialPhase === "tour") onStart?.(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPhase]);

  useEffect(() => {
    onHighlight?.(open && phase === "tour" ? STEPS[i]?.key : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phase, i]);

  if (!open) return null;

  const startTour = () => { setPhase("tour"); setI(0); onStart?.(); };
  const next = () => { if (i < STEPS.length - 1) setI(i + 1); else { onComplete?.(); setPhase("checklist"); } };
  const prev = () => setI((v) => Math.max(0, v - 1));
  const step = STEPS[i] || STEPS[0];
  const Icon = step.icon;

  const ui = (
    <div className="fixed inset-0 z-[10001]" data-testid="onboarding-tour" role="dialog" aria-modal="true">
      <div className="absolute inset-0 lg:left-64 bg-black/75 backdrop-blur-sm" onClick={phase === "welcome" ? undefined : next} />
      <div className="absolute inset-0 lg:left-64 flex items-stretch sm:items-center justify-center sm:p-6">
        <div className="relative w-full h-full sm:h-auto sm:max-w-lg bg-[#101015] border-0 sm:border border-white/10 sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}>
          {/* Kopf */}
          <div className="px-6 pt-6 sm:pt-6 flex items-center justify-between gap-3 shrink-0">
            <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: accent }}>
              <Sparkles className="w-4 h-4" /> {t("Cockpit-Rundgang")}
            </span>
            <button onClick={() => (phase === "checklist" ? onClose?.() : onDismiss?.(false))} data-testid="onboarding-close" aria-label={t("Später ansehen")}
              className="p-2 -mr-2 text-gray-500 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
          </div>

          {phase === "welcome" ? (
            <div className="flex-1 flex flex-col justify-center px-6 py-8 sm:py-6" data-testid="onboarding-welcome">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5" style={{ background: `${accent}1F` }}>
                <LayoutGrid className="w-8 h-8" style={{ color: accent }} />
              </div>
              <h2 className="text-2xl sm:text-[26px] font-black tracking-tight leading-tight">{t("Willkommen im KEWAGPage Kunden-Cockpit")}</h2>
              <p className="text-sm text-gray-300 mt-3 leading-relaxed">
                {t("Wir zeigen Ihnen Schritt für Schritt jeden Bereich Ihres Cockpits — und genau, was Sie dort tun können. So wissen Sie von A bis Z, wo Sie was finden und erledigen.")}
              </p>
              <div className="mt-8 space-y-2.5">
                <button onClick={startTour} data-testid="onboarding-start"
                  className="w-full py-3.5 rounded-xl text-black font-bold text-sm transition-transform active:scale-[0.98] hover:brightness-105" style={{ background: accent }}>
                  {t("Rundgang starten")}
                </button>
                <button onClick={() => onDismiss?.(false)} data-testid="onboarding-later"
                  className="w-full py-3 rounded-xl bg-white/5 border border-white/10 text-white font-semibold text-sm hover:bg-white/10 transition-colors">
                  {t("Später ansehen")}
                </button>
                <button onClick={() => onDismiss?.(true)} data-testid="onboarding-never"
                  className="w-full py-2 text-xs text-gray-500 hover:text-gray-300 transition-colors">
                  {t("Nicht mehr anzeigen")}
                </button>
              </div>
            </div>
          ) : phase === "checklist" ? (
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-8 sm:py-7" data-testid="onboarding-checklist">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5" style={{ background: `${accent}1F` }}>
                <Sparkles className="w-8 h-8" style={{ color: accent }} />
              </div>
              <h2 className="text-2xl font-black tracking-tight leading-tight">{t("Geschafft — jetzt loslegen")}</h2>
              <p className="text-sm text-gray-300 mt-2 mb-5 leading-relaxed">{t("So kommen Sie sofort ins Tun — erledigen Sie diese kurzen Schritte.")}</p>
              {loadRecommendation && (
                <FirstRecommendation load={loadRecommendation} accent={accent} onGo={(to) => onGo?.(to)} onAskAi={() => onOpenAi?.()} />
              )}
              <GettingStarted items={checklist} accent={accent} variant="tour" onGo={(to) => onGo?.(to)} />
              <button onClick={() => onClose?.()} data-testid="onboarding-checklist-done"
                className="w-full mt-6 py-3.5 rounded-xl text-black font-bold text-sm transition-transform active:scale-[0.98] hover:brightness-105" style={{ background: accent }}>
                {t("Los geht's")}
              </button>
            </div>
          ) : (
            <>
              <div className="flex-1 min-h-0 overflow-y-auto px-6 py-8 sm:py-7" data-testid="onboarding-step" data-step={i}>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5" style={{ background: `${accent}1F` }}>
                  <Icon className="w-8 h-8" style={{ color: accent }} />
                </div>
                <div className="text-xs font-semibold text-gray-500 mb-1.5">{t("Schritt")} {i + 1} {t("von")} {STEPS.length}</div>
                <h2 className="text-2xl font-black tracking-tight leading-tight" data-testid="onboarding-step-title">{t(step.title)}</h2>
                <p className="text-sm text-gray-300 mt-3 leading-relaxed" data-testid="onboarding-step-text">{t(step.text)}</p>
                {(SECTION_GUIDE[guideKeyFor(step.key)] || []).length > 0 && (
                  <div className="mt-4" data-testid="onboarding-step-todo">
                    <div className="text-[11px] font-bold uppercase tracking-[0.16em] mb-2" style={{ color: accent }}>{t("Das tun Sie hier")}</div>
                    <ul className="space-y-1.5">
                      {(SECTION_GUIDE[guideKeyFor(step.key)] || []).map((b, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-[13px] text-gray-300">
                          <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accent }} /><span>{t(b)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {i === STEPS.length - 1 && (
                  <button onClick={() => { onOpenAi?.(); }} data-testid="onboarding-ask"
                    className="mt-5 inline-flex items-center gap-2 text-sm font-semibold self-start px-4 py-2.5 rounded-xl border transition-colors hover:bg-white/5"
                    style={{ borderColor: `${accent}55`, color: accent }}>
                    <MessageCircle className="w-4 h-4" /> {t("Frage stellen")}
                  </button>
                )}
              </div>

              {/* Fortschritt + Navigation */}
              <div className="px-6 pb-6 pt-2 shrink-0">
                <div className="flex items-center justify-center gap-1.5 mb-4">
                  {STEPS.map((_, idx) => (
                    <span key={idx} className="h-1.5 rounded-full transition-all" data-testid={idx === i ? "onboarding-dot-active" : undefined}
                      style={{ width: idx === i ? 22 : 6, background: idx === i ? accent : "rgba(255,255,255,0.18)" }} />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={prev} disabled={i === 0} data-testid="onboarding-back"
                    className="flex items-center justify-center gap-1.5 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white font-semibold text-sm hover:bg-white/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    <ChevronLeft className="w-4 h-4" /> {t("Zurück")}
                  </button>
                  <button onClick={next} data-testid="onboarding-next"
                    className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl text-black font-bold text-sm transition-transform active:scale-[0.98] hover:brightness-105" style={{ background: accent }}>
                    {i === STEPS.length - 1 ? <>{t("Fertig")} <Check className="w-4 h-4" /></> : <>{t("Weiter")} <ChevronRight className="w-4 h-4" /></>}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(ui, document.body);
}
